import React, { useEffect, useState, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAdminXperiences, updateAdminXperienceStatus, Xperience } from '../../services/api';
import { t } from '../../i18n';
import './ModerationPage.css';

const ADMIN_XPERIENCES_STATE_KEY = 'admin_xperiences_list_state_v1';

const getStoredAdminXperiencesState = () => {
  try {
    const raw = sessionStorage.getItem(ADMIN_XPERIENCES_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const AdminXperiencesPage: React.FC = () => {
  const { creator } = useAuth();
  const navigate = useNavigate();
  const storedState = getStoredAdminXperiencesState();
  const [xperiences, setXperiences] = useState<Xperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | ''>(
    storedState?.statusFilter === 'approved' || storedState?.statusFilter === 'rejected' || storedState?.statusFilter === ''
      ? storedState.statusFilter
      : 'pending'
  );
  const [searchQuery, setSearchQuery] = useState(String(storedState?.searchQuery || ''));
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [page, setPage] = useState(Math.max(Number(storedState?.page) || 1, 1));
  const [pageSize, setPageSize] = useState(() => {
    const candidate = Number(storedState?.pageSize);
    return [12, 24, 48].includes(candidate) ? candidate : 12;
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [usesServerPagination, setUsesServerPagination] = useState(false);

  useEffect(() => {
    fetchXperiences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, pageSize]);

  useEffect(() => {
    sessionStorage.setItem(
      ADMIN_XPERIENCES_STATE_KEY,
      JSON.stringify({
        statusFilter,
        searchQuery,
        page,
        pageSize
      })
    );
  }, [statusFilter, searchQuery, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const fetchXperiences = async () => {
    try {
      setLoading(true);
      const response = await getAdminXperiences(statusFilter, { page, limit: pageSize });
      setXperiences(response.data.xperiences);
      const hasPagination = !!response.data.pagination;
      setUsesServerPagination(hasPagination);

      if (hasPagination) {
        setTotalItems(response.data.pagination?.totalItems || 0);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        const localTotal = response.data.xperiences.length;
        setTotalItems(localTotal);
        setTotalPages(Math.max(Math.ceil(localTotal / pageSize), 1));
      }
    } catch (err: any) {
      setError(t('adminXperiences.error.fetch'));
    } finally {
      setLoading(false);
    }
  };

  const filteredXperiences = useMemo(() => {
    if (!searchQuery.trim()) return xperiences;
    const lowerQuery = searchQuery.toLowerCase();
    
    return xperiences.filter(x => {
      const titleMatch = x.title?.toLowerCase().includes(lowerQuery);
      const descMatch = x.description?.toLowerCase().includes(lowerQuery);
      
      const creatorData = (x as any).creatorUser;
      const creatorMatch = creatorData?.name?.toLowerCase().includes(lowerQuery) || 
                           creatorData?.alias?.toLowerCase().includes(lowerQuery) || 
                           creatorData?.email?.toLowerCase().includes(lowerQuery);
                           
      return titleMatch || descMatch || creatorMatch;
    });
  }, [xperiences, searchQuery]);

  const pagedXperiences = useMemo(() => {
    if (usesServerPagination) {
      return filteredXperiences;
    }

    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredXperiences.slice(start, end);
  }, [filteredXperiences, page, pageSize, usesServerPagination]);

  const hasActiveSearch = searchQuery.trim().length > 0;
  const uiTotalItems = hasActiveSearch ? filteredXperiences.length : (usesServerPagination ? totalItems : filteredXperiences.length);
  const uiTotalPages = hasActiveSearch
    ? Math.max(Math.ceil(filteredXperiences.length / pageSize), 1)
    : (usesServerPagination ? totalPages : Math.max(Math.ceil(filteredXperiences.length / pageSize), 1));

  const handleStatusChange = async (id: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      await updateAdminXperienceStatus(id, newStatus);
      setFeedbackMsg({ type: 'success', text: `${t('adminXperiences.success.markedAs')} ${t(`adminXperiences.filter.${newStatus}`)}` });
      await fetchXperiences();
      setTimeout(() => setFeedbackMsg(null), 3000);
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: t('adminXperiences.error.updateStatus') });
      setTimeout(() => setFeedbackMsg(null), 3000);
    }
  };

  if (!creator || !['manager', 'administrator'].includes(creator.role || '')) {
    return <Navigate to="/" />;
  }

  return (
    <div className="moderation-page">
      <div className="moderation-header">
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={() => window.location.href = '/'} 
            style={{
              background: 'transparent', 
              border: '1px solid var(--border-color)', 
              color: 'var(--text-secondary)', 
              padding: '8px 16px', 
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 500
            }}
          >
            ← {t('adminXperiences.backDashboard')}
          </button>
        </div>
        <h1>{t('adminXperiences.title')}</h1>
        
        <div className="moderation-controls">
          <div className="search-bar" style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder={t('adminXperiences.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{ paddingRight: '36px' }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={t('common.clearSearch')}
              >
                ✕
              </button>
            )}
          </div>

          <div className="moderation-filters">
            <button 
              className={`filter-btn ${statusFilter === '' ? 'active' : ''}`}
              onClick={() => {
                setStatusFilter('');
                setPage(1);
              }}
            >
              {t('adminXperiences.filter.all')}
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => {
                setStatusFilter('pending');
                setPage(1);
              }}
            >
              {t('adminXperiences.filter.pending')}
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'approved' ? 'active' : ''}`}
              onClick={() => {
                setStatusFilter('approved');
                setPage(1);
              }}
            >
              {t('adminXperiences.filter.approved')}
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => {
                setStatusFilter('rejected');
                setPage(1);
              }}
            >
              {t('adminXperiences.filter.rejected')}
            </button>
          </div>
        </div>
      </div>

      {feedbackMsg && (
        <div className={`feedback-message ${feedbackMsg.type}`}>
          {feedbackMsg.text}
        </div>
      )}

      {error && <div className="feedback-message error">{error}</div>}

      {loading ? (
        <div className="loading-state">{t('adminXperiences.loading')}</div>
      ) : filteredXperiences.length === 0 ? (
        <div className="empty-state">
          <p>{xperiences.length > 0 ? t('adminXperiences.empty.search') : t('adminXperiences.empty.filter')}</p>
        </div>
      ) : (
        <>
          <div className="media-moderation-grid">
            {pagedXperiences.map((xperience) => {
            const firstMedia = xperience.mediaLinks?.[0];
            const posterUrl = firstMedia?.posterUrl;
            const fullPosterUrl = posterUrl ? `https://x-perienc.es/${posterUrl}` : '';

              return (
                <div key={xperience.id} className={`moderation-card status-${xperience.status}`}>
                <div 
                  className="media-thumbnail" 
                  style={{ backgroundImage: `url(${fullPosterUrl})` }}
                >
                  {!posterUrl && <div className="no-poster">{t('adminXperiences.noPoster')}</div>}
                  <div className="status-badge">{t(`adminXperiences.filter.${xperience.status}`)}</div>
                </div>
                
                <div className="media-info">
                  <h3>{xperience.title}</h3>
                  <p className="media-desc" style={{ WebkitLineClamp: 2 }}>{xperience.description || t('adminXperiences.noDescription')}</p>
                  
                  <div className="media-meta">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                          {t('adminXperiences.creator')}: {(xperience as any).creatorUser?.name || (xperience as any).creatorUser?.alias || (xperience as any).creator}
                      </span>
                      <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                          {t('adminXperiences.email')}: {(xperience as any).creatorUser?.email || t('adminXperiences.na')}
                      </span>
                      <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                          {t('adminXperiences.date')}: {xperience.createdDate ? new Date(xperience.createdDate).toLocaleDateString() : t('adminXperiences.na')}
                      </span>
                      <span style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginTop: '4px' }}>
                          {t('adminXperiences.mediaCount')}: <strong>{xperience.mediaLinks?.length || 0} {t('adminXperiences.items')}</strong>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="moderation-actions">
                  <button 
                    className="action-btn"
                    style={{ background: 'var(--primary-color)', color: 'white', fontWeight: 'bold' }}
                    onClick={() => navigate(`/moderation?xperienceId=${xperience.id}`)}
                  >
                      {t('adminXperiences.action.moderateMedia')}
                  </button>
                  {xperience.status !== 'approved' && (
                    <button 
                      className="action-btn approve"
                      onClick={() => handleStatusChange(xperience.id, 'approved')}
                    >
                        {t('adminXperiences.action.approveAll')}
                    </button>
                  )}
                  {xperience.status !== 'rejected' && (
                    <button 
                      className="action-btn reject"
                      onClick={() => handleStatusChange(xperience.id, 'rejected')}
                    >
                        {t('adminXperiences.action.rejectAll')}
                    </button>
                  )}
                  {xperience.status !== 'pending' && (
                    <button 
                      className="action-btn pending"
                      onClick={() => handleStatusChange(xperience.id, 'pending')}
                    >
                        {t('adminXperiences.action.setPending')}
                    </button>
                  )}
                </div>
                </div>
              );
            })}
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            marginTop: '20px',
            flexWrap: 'wrap'
          }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              {t('adminCreators.pagination.total')}: <strong>{uiTotalItems}</strong> | {t('adminCreators.pagination.page')} <strong>{Math.min(page, uiTotalPages)}</strong> {t('adminCreators.pagination.of')} <strong>{uiTotalPages}</strong>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{t('adminCreators.pagination.rowsPerPage')}:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                style={{
                  minWidth: '86px',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
              >
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
              </select>

              <button
                className="filter-btn"
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page <= 1}
              >
                {t('adminCreators.pagination.previous')}
              </button>
              <button
                className="filter-btn"
                onClick={() => setPage(prev => Math.min(prev + 1, uiTotalPages))}
                disabled={page >= uiTotalPages}
              >
                {t('adminCreators.pagination.next')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminXperiencesPage;